import { createFileRoute } from '@tanstack/react-router'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useState } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export const Route = createFileRoute(
  '/_authenticated/_layout/settings/_layout/reports',
)({
  component: ReportsSettings,
})

function ReportsSettings() {
  const [emailEnabled, setEmailEnabled] = useState(false)
  const [slackEnabled, setSlackEnabled] = useState(false)
  const [emailFrequency, setEmailFrequency] = useState('weekly')
  const [slackFrequency, setSlackFrequency] = useState('weekly')
  const [emailRecipients, setEmailRecipients] = useState('')
  const [slackChannel, setSlackChannel] = useState('')

  return (
    <div className="space-y-6 py-6">
      <div className="space-y-0.5">
        <h2 className="text-2xl font-semibold tracking-tight">
          Reports Settings
        </h2>
        <p className="text-muted-foreground">
          Configure how and when you receive campaign performance reports
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Email Reports */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle>Email Reports</CardTitle>
              <Switch
                checked={emailEnabled}
                onCheckedChange={setEmailEnabled}
                aria-label="Toggle email reports"
              />
            </div>
            <CardDescription>
              Automatically send campaign performance reports to your email
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email-recipients">Recipients</Label>
              <Input
                id="email-recipients"
                placeholder="email1@company.com, email2@company.com"
                value={emailRecipients}
                onChange={(e) => setEmailRecipients(e.target.value)}
                disabled={!emailEnabled}
              />
              <p className="text-xs text-muted-foreground">
                Separate multiple email addresses with commas
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-frequency">Frequency</Label>
              <Select
                value={emailFrequency}
                onValueChange={setEmailFrequency}
                disabled={!emailEnabled}
              >
                <SelectTrigger id="email-frequency">
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button disabled={!emailEnabled}>Save Email Settings</Button>
          </CardContent>
        </Card>

        {/* Slack Integration */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle>Slack Integration</CardTitle>
              <Switch
                checked={slackEnabled}
                onCheckedChange={setSlackEnabled}
                aria-label="Toggle slack integration"
              />
            </div>
            <CardDescription>
              Send campaign performance reports directly to your Slack channels
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="slack-channel">Channel</Label>
              <Input
                id="slack-channel"
                placeholder="#marketing-reports"
                value={slackChannel}
                onChange={(e) => setSlackChannel(e.target.value)}
                disabled={!slackEnabled}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slack-frequency">Frequency</Label>
              <Select
                value={slackFrequency}
                onValueChange={setSlackFrequency}
                disabled={!slackEnabled}
              >
                <SelectTrigger id="slack-frequency">
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!slackEnabled ? (
              <Button disabled>Save Slack Settings</Button>
            ) : (
              <div className="space-y-4">
                <Button>Connect to Slack</Button>
                <p className="text-xs text-muted-foreground">
                  You'll be redirected to Slack to authorize this integration
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Report Content Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Report Content</CardTitle>
          <CardDescription>
            Choose what information to include in your automated reports
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Switch id="campaigns-overview" defaultChecked />
              <Label htmlFor="campaigns-overview">Campaigns Overview</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch id="performance-metrics" defaultChecked />
              <Label htmlFor="performance-metrics">Performance Metrics</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch id="conversion-data" defaultChecked />
              <Label htmlFor="conversion-data">Conversion Data</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch id="top-keywords" defaultChecked />
              <Label htmlFor="top-keywords">Top Keywords</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch id="budget-tracking" defaultChecked />
              <Label htmlFor="budget-tracking">Budget Tracking</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch id="recommendations" defaultChecked />
              <Label htmlFor="recommendations">Recommendations</Label>
            </div>
          </div>

          <Button className="mt-4">Save Content Settings</Button>
        </CardContent>
      </Card>
    </div>
  )
}
